import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import { Bold, Italic, Underline, Heading2, List, ListOrdered, Quote, Link2, ImagePlus, Undo2, Redo2 } from "lucide-react";
import { cn } from "@/lib/utils";

const Btn = ({ on, active, label, children, testId }) => (
  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={on} aria-label={label} title={label} data-testid={testId}
    className={cn("h-8 w-8 grid place-items-center rounded-lg transition-colors", active ? "bg-primary text-primary-foreground" : "hover:bg-secondary")}>
    {children}
  </button>
);

const askUrl = (msg) => {
  const url = window.prompt(msg, "https://");
  return url && url.startsWith("https://") ? url : null;
};

export const RichEditor = ({ value, onChange, tags = [], testId = "rich-editor", minHeight = 180 }) => {
  const editor = useEditor({
    extensions: [StarterKit.configure({ link: { openOnClick: false, protocols: ["https", "mailto"] } }), Image],
    content: value || "",
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
    editorProps: { attributes: { class: "rich-content focus:outline-none px-4 py-3", style: `min-height:${minHeight}px` } },
  });
  if (!editor) return null;
  const c = () => editor.chain().focus();
  const link = () => {
    const url = askUrl("Link URL (https://...)");
    if (url) c().extendMarkRange("link").setLink({ href: url }).run();
  };
  const image = () => {
    const url = askUrl("Image URL (https://...)");
    if (url) c().setImage({ src: url }).run();
  };
  return (
    <div className="rounded-xl border border-input bg-background overflow-hidden" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-1 border-b border-border p-1.5">
        <Btn on={() => c().toggleBold().run()} active={editor.isActive("bold")} label="Bold" testId={`${testId}-bold`}><Bold className="h-4 w-4" /></Btn>
        <Btn on={() => c().toggleItalic().run()} active={editor.isActive("italic")} label="Italic"><Italic className="h-4 w-4" /></Btn>
        <Btn on={() => c().toggleUnderline().run()} active={editor.isActive("underline")} label="Underline"><Underline className="h-4 w-4" /></Btn>
        <Btn on={() => c().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} label="Heading"><Heading2 className="h-4 w-4" /></Btn>
        <Btn on={() => c().toggleBulletList().run()} active={editor.isActive("bulletList")} label="Bullet list"><List className="h-4 w-4" /></Btn>
        <Btn on={() => c().toggleOrderedList().run()} active={editor.isActive("orderedList")} label="Numbered list"><ListOrdered className="h-4 w-4" /></Btn>
        <Btn on={() => c().toggleBlockquote().run()} active={editor.isActive("blockquote")} label="Quote"><Quote className="h-4 w-4" /></Btn>
        <Btn on={link} active={editor.isActive("link")} label="Link" testId={`${testId}-link`}><Link2 className="h-4 w-4" /></Btn>
        <Btn on={image} label="Image from URL" testId={`${testId}-image`}><ImagePlus className="h-4 w-4" /></Btn>
        <Btn on={() => c().undo().run()} label="Undo"><Undo2 className="h-4 w-4" /></Btn>
        <Btn on={() => c().redo().run()} label="Redo"><Redo2 className="h-4 w-4" /></Btn>
      </div>
      <EditorContent editor={editor} data-testid={`${testId}-content`} />
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 border-t border-border p-2" data-testid={`${testId}-tags`}>
          <span className="text-xs font-bold text-muted-foreground self-center mr-1">Insert tag:</span>
          {tags.map((t) => (
            <button key={t} type="button" onClick={() => c().insertContent(`{${t}}`).run()} data-testid={`${testId}-tag-${t}`}
              className="rounded-full border border-purple-500/40 bg-purple-500/10 px-2 py-0.5 text-xs font-mono hover:bg-purple-500/20">{`{${t}}`}</button>
          ))}
        </div>
      )}
    </div>
  );
};
